import { MONTH_OPTIONS, monthLabel, type MonthRef } from "../lib/dates";

interface Props {
  month: MonthRef;
  onStep: (delta: number) => void;
  onChange: (month: MonthRef) => void;
  /** Rendered on the right of the bar, e.g. the month total or an export button. */
  children?: React.ReactNode;
}

const YEAR_SPAN = 6;

/**
 * Back/forward arrows plus a direct month/year jump, so any past month is reachable in one
 * step rather than by clicking backwards repeatedly.
 */
export function MonthNav({ month, onStep, onChange, children }: Props): JSX.Element {
  const thisYear = new Date().getFullYear();
  const years = Array.from(
    { length: YEAR_SPAN + 2 },
    (_, index) => thisYear - YEAR_SPAN + index + 1,
  );

  return (
    <div className="month-nav">
      <div className="month-nav-controls">
        <button type="button" className="ghost icon" aria-label="Previous month" onClick={() => onStep(-1)}>
          ‹
        </button>
        <strong className="month-title">{monthLabel(month)}</strong>
        <button type="button" className="ghost icon" aria-label="Next month" onClick={() => onStep(1)}>
          ›
        </button>

        <select
          aria-label="Jump to month"
          value={month.month}
          onChange={(event) => onChange({ ...month, month: Number(event.target.value) })}
        >
          {MONTH_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Jump to year"
          value={month.year}
          onChange={(event) => onChange({ ...month, year: Number(event.target.value) })}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className="month-nav-extra">{children}</div>
    </div>
  );
}
